import { CellFormulaValue, CellHyperlinkValue, CellValue, Worksheet } from "exceljs";

export interface IParsedHeader {
  id: string;
  label: string;
}

export type IValidatedPosRow = Record<string, any> & {
  _status: "valid" | "invalid";
  _error?: string;
};

// Fixed template columns (1-based), per the field order defined in the requirement.
// Adjust here if the backend-generated template uses a different column order.
export const POS_COLUMN_INDEX = {
  posCode: 1,
  companyName: 2,
  subRetail: 3,
  idTier: 4,
  pointOfSale: 5,
  posClass: 6,
  online: 7,
  state: 8,
  city: 9,
  country: 10,
  posFormat: 11,
  longitude: 12,
  latitude: 13,
  stratification: 14,
  cnpj: 15,
} as const;

// Backend-enforced cap per upload (also shown to users via modal.posBulkUploadMaxLimit);
// exceeding it causes a server-side 500 instead of a clean validation error, so it must be
// checked client-side before the file ever reaches parsePosWorksheet's row-level validation.
export const MAX_POS_RECORDS = 100;

// Case/accent/whitespace-insensitive normalization, used to match a parsed header's text
// against a field's known label candidates (resolveColumnIndexes), so differences in casing,
// accents, or spacing between the template's actual header text and our known labels don't
// cause a column to be misidentified.
export const normalizeLabel = (value: string | undefined): string =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// Known header text per field (en/es/pt labels plus observed backend-template variants),
// used to locate a field's real column when the template's column order/content drifts
// from POS_COLUMN_INDEX instead of silently misreading the wrong cell.
const HEADER_LABEL_CANDIDATES: Record<keyof typeof POS_COLUMN_INDEX, string[]> = {
  posCode: ["POS Code", "Código punto de venta", "Código do Ponto de Venda"],
  companyName: ["Company Name", "Nombre de la compañía", "Nome da Empresa"],
  subRetail: ["Sub Retail"],
  idTier: ["Tier", "Nível", "ID TIER", "Id Tier"],
  pointOfSale: [
    "Point of sale",
    "Point of Sale Name",
    "Punto de venta",
    "Nombre del punto de venta",
    "Ponto de venda",
    "Nome do Ponto de Venda",
  ],
  posClass: ["Class", "Clase", "Classe"],
  online: ["Online", "On-line", "En linea", "En línea"],
  state: ["State", "Estado"],
  city: ["City", "Ciudad", "Cidade"],
  country: ["Country", "País"],
  posFormat: ["Format", "Formato", "POSFormat", "POS Format", "PosFormat"],
  longitude: ["Longitude", "Longitud"],
  latitude: ["Latitude", "Latitud"],
  stratification: ["Socioeconomic Level", "Nivel socioeconómico", "Nível Socioeconômico", "Stratification"],
  cnpj: ["CNPJ"],
};

const HEADER_LABEL_ALIASES: Record<keyof typeof POS_COLUMN_INDEX, string[]> = Object.fromEntries(
  Object.entries(HEADER_LABEL_CANDIDATES).map(([key, labels]) => [key, labels.map(normalizeLabel)])
) as Record<keyof typeof POS_COLUMN_INDEX, string[]>;

// Resolves each field to the worksheet column matching its header text, falling back to the
// fixed POS_COLUMN_INDEX position only when no header text match is found.
export const resolveColumnIndexes = (headers: IParsedHeader[]): Record<keyof typeof POS_COLUMN_INDEX, number> => {
  const columnByNormalizedLabel = new Map<string, number>();
  headers.forEach((header) => {
    const normalized = normalizeLabel(header.label);
    if (normalized && !columnByNormalizedLabel.has(normalized)) {
      columnByNormalizedLabel.set(normalized, Number(header.id));
    }
  });

  const resolved = {} as Record<keyof typeof POS_COLUMN_INDEX, number>;
  (Object.keys(POS_COLUMN_INDEX) as (keyof typeof POS_COLUMN_INDEX)[]).forEach((key) => {
    const matchedAlias = HEADER_LABEL_ALIASES[key].find((alias) => columnByNormalizedLabel.has(alias));
    resolved[key] = matchedAlias !== undefined ? columnByNormalizedLabel.get(matchedAlias)! : POS_COLUMN_INDEX[key];
  });

  return resolved;
};

const MANDATORY_FIELDS: { key: keyof typeof POS_COLUMN_INDEX; labelKey: string }[] = [
  { key: "posCode", labelKey: "posManagement.posCode" },
  { key: "subRetail", labelKey: "posManagement.subRetail" },
  { key: "pointOfSale", labelKey: "posManagement.pointOfSale" },
  { key: "posClass", labelKey: "posManagement.class" },
  { key: "online", labelKey: "posManagement.online" },
  { key: "state", labelKey: "header.state" },
  { key: "city", labelKey: "posManagement.city" },
  { key: "country", labelKey: "header.country" },
  { key: "longitude", labelKey: "posManagement.longitude" },
  { key: "latitude", labelKey: "posManagement.latitude" },
];

const LATITUDE_RANGE: [number, number] = [-90, 90];
const LONGITUDE_RANGE: [number, number] = [-180, 180];
const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

const toTrimmedString = (value: unknown): string | undefined =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? value.toString().trim()
    : undefined;

const getValueCellTemplate = (cellValue: CellValue): string | undefined => {
  if (typeof cellValue === "string" || typeof cellValue === "number") {
    return cellValue?.toString().trim();
  }

  if ((cellValue as CellHyperlinkValue)?.text) {
    return (cellValue as CellHyperlinkValue)?.text?.toString().trim();
  }

  if ((cellValue as CellFormulaValue)?.result) {
    return toTrimmedString((cellValue as CellFormulaValue)?.result);
  }
};

// Step 1: generic, template-agnostic header/row extraction (no date columns in this template).
export const parsePosWorksheet = (
  sheet: Worksheet
): { headers: IParsedHeader[]; rows: Record<string, any>[] } => {
  const headers: IParsedHeader[] = [];
  const headerRow = sheet.getRow(1);
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const label = getValueCellTemplate(cell.value) ?? `${colNumber}`;
    headers.push({ id: String(colNumber), label });
  });

  const rows: Record<string, any>[] = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) {
      return;
    }

    if (!getValueCellTemplate(row.getCell(1).value)) {
      return;
    }

    const rowData: Record<string, any> = { _rowNumber: rowNumber };
    headers.forEach((header) => {
      const colNumber = Number(header.id);
      rowData[header.id] = getValueCellTemplate(row.getCell(colNumber).value);
    });

    rows.push(rowData);
  });

  return { headers, rows };
};

const parseOnlineFlag = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const normalized = value.trim().toUpperCase();
  if (["YES", "SI", "SÍ", "1", "TRUE"].includes(normalized)) return 1;
  if (["NO", "0", "FALSE"].includes(normalized)) return 0;
  return undefined;
};

const isValidCoordinate = (value: string | undefined, range: [number, number]): boolean => {
  if (!value || !DECIMAL_PATTERN.test(value)) return false;
  const numeric = Number(value);
  return numeric >= range[0] && numeric <= range[1];
};

// Step 3: business validation rules, scoped per row instead of aborting the whole parse on the
// first failure. A second pass then rejects all rows sharing a duplicate Post Code. Catalog
// correctness (Sub Retail/Country/State/City) is validated server-side only and surfaced via
// the submission result/error report — the raw file is submitted as-is, so a client-resolved
// catalog match was never actually consumed by the payload.
export const validatePosRows = (
  rows: Record<string, any>[],
  translate: (key: string) => string,
  columnIndex: Record<keyof typeof POS_COLUMN_INDEX, number> = POS_COLUMN_INDEX
): IValidatedPosRow[] => {
  const structurallyValidated = rows.map((row) => {
    const rowNumber = row["_rowNumber"];
    const get = (key: keyof typeof POS_COLUMN_INDEX) => row[String(columnIndex[key])];

    const missingField = MANDATORY_FIELDS.find(({ key }) => !get(key));
    if (missingField) {
      return {
        ...row,
        _status: "invalid" as const,
        _error: translate("modal.posMissingMandatoryField")
          .replace("{0}", String(rowNumber))
          .replace("{1}", translate(missingField.labelKey)),
      };
    }

    const onLine = parseOnlineFlag(get("online"));
    if (onLine === undefined) {
      return {
        ...row,
        _status: "invalid" as const,
        _error: translate("modal.posMissingMandatoryField")
          .replace("{0}", String(rowNumber))
          .replace("{1}", translate("posManagement.online")),
      };
    }

    if (!isValidCoordinate(get("latitude"), LATITUDE_RANGE) || !isValidCoordinate(get("longitude"), LONGITUDE_RANGE)) {
      return {
        ...row,
        _status: "invalid" as const,
        _error: translate("modal.posInvalidCoordinate").replace("{0}", String(rowNumber)),
      };
    }

    return {
      ...row,
      _status: "valid" as const,
    };
  });

  const postCodeCounts = structurallyValidated.reduce((acc, row) => {
    if (row._status === "valid") {
      const posCode = (row as Record<string, any>)[String(columnIndex.posCode)];
      acc[posCode] = (acc[posCode] ?? 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  return structurallyValidated.map((row): IValidatedPosRow => {
    const posCode = (row as Record<string, any>)[String(columnIndex.posCode)];
    if (row._status === "valid" && postCodeCounts[posCode] > 1) {
      return {
        ...row,
        _status: "invalid",
        _error: translate("modal.posDuplicatePostCodeFile")
          .replace("{0}", String((row as Record<string, any>)["_rowNumber"]))
          .replace("{1}", posCode),
      };
    }
    return row;
  });
};
